import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useSearchParams } from 'react-router-dom'
import { SquareCheckBig } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { BrowseProjectCard } from '@/components/discovery/browse-project-card'
import { BrowseCardGrid } from '@/components/discovery/browse-card-grid'
import { BrowseInspirationGrid } from '@/components/discovery/browse-inspiration-grid'
import { BrowseSearchBar } from '@/components/discovery/browse-search-bar'
import { BrowseViewSettings } from '@/components/discovery/browse-view-settings'
import { useBrowseInfinite } from '@/features/discovery/use-browse-infinite'
import { useBrowseMode } from '@/features/discovery/use-browse-mode'
import { useBrowseColumns } from '@/features/discovery/use-browse-columns'
import { useBrowseTileSizes } from '@/features/discovery/use-browse-tile-sizes'
import { useInfiniteScrollSentinel } from '@/features/discovery/use-infinite-scroll-sentinel'
import { useMyFavoritedIds } from '@/features/favorites/use-favorites'

const PAGE_SIZE = 20

export const BrowsePage = () => {
  const [searchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''

  const {
    data: pages,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBrowseInfinite({
    q: q || undefined,
    sort: 'recent',
    size: PAGE_SIZE,
  })

  // Flatten all loaded pages into a single item list for the grids. Memoize so prop identity
  // stays stable across renders when nothing new has loaded.
  const items = useMemo(() => pages?.flatMap((p) => p.items) ?? [], [pages])
  const echoQuery = pages?.[0]?.query ?? null

  const [mode, setMode] = useBrowseMode()
  const [columns, setColumns] = useBrowseColumns()
  const [enabledSizes, toggleSize] = useBrowseTileSizes()
  // Expanded cards — clicking a card "lifts" it into a preview state with an info overlay
  // and an explicit "Show more" link. Multi-expand: cards expand independently of each
  // other, the user collapses individually via the collapse icon or by clicking the photo
  // of an expanded card. Resets when the user changes mode or search so stale previews
  // don't carry across contexts.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    setExpandedIds(new Set())
  }, [mode, q])
  const handleToggleExpanded = useCallback((projectId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
      } else {
        next.add(projectId)
      }
      return next
    })
  }, [])

  // Phase 10: bulk-fetch which of these projects the viewer has favorited so each card can
  // render its heart in the right state without N+1 round-trips. Anonymous viewers don't get
  // hearts on browse cards (UI_PLAN §4) so the query is gated on authentication.
  const auth = useAuth()
  const projectIds = items.map((p) => p.id)
  const favoritedIds = useMyFavoritedIds(projectIds, auth.isAuthenticated)
  const isFavoritedByMe = (projectId: string) => favoritedIds.data?.has(projectId) ?? false

  // Infinite-scroll sentinel — kicks off `fetchNextPage` 200px before the bottom of the list
  // enters the viewport. The `enabled` predicate gates against double-fires while a fetch is
  // already in flight.
  const onIntersect = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])
  const sentinelRef = useInfiniteScrollSentinel({
    onIntersect,
    enabled: Boolean(hasNextPage) && !isFetchingNextPage,
  })

  // List mode wants the existing narrow column for readability. Card + Inspiration modes get
  // the full viewport (modulo a small px-4 gutter) — the mood-board feel only works when the
  // grid actually fills the page.
  const containerClass =
    mode === 'list'
      ? 'mx-auto w-full max-w-3xl space-y-4'
      : 'mx-auto w-full max-w-none px-4 space-y-4'

  return (
    <div className={containerClass}>
      <div className="sticky top-0 z-10 -mx-4 flex items-center gap-2 bg-muted/30 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex-1">
          <BrowseSearchBar />
        </div>
        <BrowseViewSettings
          mode={mode}
          onModeChange={setMode}
          columns={columns}
          onColumnsChange={setColumns}
          enabledSizes={enabledSizes}
          onToggleSize={toggleSize}
        />
      </div>

      {/* "Step checked off the journey" pennant. Centered, not sticky — scrolls away with
          the rest of the page content as the user moves deeper into the grid. */}
      <div className="flex items-center justify-center gap-3 py-6">
        <SquareCheckBig className="h-7 w-7 text-primary" aria-hidden />
        <span className="text-2xl font-semibold tracking-tight">Get Inspired</span>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load browse</AlertTitle>
          <AlertDescription>
            Try again — if it keeps failing, the backend may be unreachable.
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-2 underline underline-offset-2"
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}

      {pages && items.length === 0 && (
        <div className="rounded-md border bg-background p-8 text-center">
          {echoQuery ? (
            <>
              <p className="text-base font-medium">No matches for &ldquo;{echoQuery}&rdquo;.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Try a different word or clear the search to see recent builds.
              </p>
            </>
          ) : (
            <>
              <p className="text-base font-medium">No public projects yet.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Be the first — anything you set to Public shows up here.
              </p>
            </>
          )}
        </div>
      )}

      {items.length > 0 && (
        <>
          {mode === 'list' && (
            <div className="space-y-3">
              {items.map((project) => (
                <BrowseProjectCard
                  key={project.id}
                  project={project}
                  isAuthenticated={auth.isAuthenticated}
                  isFavoritedByMe={isFavoritedByMe(project.id)}
                />
              ))}
            </div>
          )}
          {mode === 'card' && (
            <BrowseCardGrid
              projects={items}
              isAuthenticated={auth.isAuthenticated}
              isFavoritedByMe={isFavoritedByMe}
              expandedIds={expandedIds}
              onToggleExpanded={handleToggleExpanded}
            />
          )}
          {mode === 'inspiration' && (
            <BrowseInspirationGrid
              projects={items}
              isAuthenticated={auth.isAuthenticated}
              isFavoritedByMe={isFavoritedByMe}
              columns={columns}
              enabledSizes={enabledSizes}
              expandedIds={expandedIds}
              onToggleExpanded={handleToggleExpanded}
            />
          )}

          {/* Sentinel + status row at the bottom. Trigger fires `fetchNextPage` slightly
              before this scrolls into view; once exhausted the row shows a subtle end-of-list
              note instead. */}
          <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
          <div className="py-6 text-center text-xs text-muted-foreground">
            {isFetchingNextPage ? (
              <span>Loading more…</span>
            ) : hasNextPage ? (
              <span>&nbsp;</span>
            ) : (
              <span>You&apos;ve reached the end.</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}
